import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Repeat, Plus, X, MapPin, Search } from "lucide-react";

const fmt = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short" }) : "—");

function AddProperty({ all, onAdd }) {
  const [pick, setPick] = useState("");
  return (
    <div className="flex gap-2 flex-wrap" data-testid="communal-add">
      <Select value={pick} onValueChange={setPick}>
        <SelectTrigger className="w-[320px] h-10" data-testid="communal-add-select"><SelectValue placeholder="Choose a property to add…" /></SelectTrigger>
        <SelectContent>
          {all.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`communal-opt-${p.id}`}>{p.address} · {p.clientName}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button disabled={!pick} className="bg-brand hover:bg-brand-dark h-10" data-testid="communal-add-btn" onClick={() => { onAdd(pick); setPick(""); }}><Plus className="h-4 w-4 mr-1" />Add to list</Button>
    </div>
  );
}

export default function CommunalList() {
  const { can } = useAuth();
  const canManage = can("property.manage");
  const [rows, setRows] = useState(null);
  const [props, setProps] = useState([]);
  const [q, setQ] = useState("");
  const load = () => client.get("/communal-list").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    client.get("/properties").then((r) => setProps(r.data || [])).catch(() => {});
  }, []);

  const toggle = async (id, on) => {
    try { await client.put(`/communal-list/${id}`, { on }); toast.success(on ? "Added to list" : "Removed from list"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setLength = async (id, hours) => {
    const minutes = Math.round(Number(hours) * 60);
    if (!(minutes >= 15)) { toast.error("Length must be at least 0.25 hours"); return; }
    try { await client.put(`/communal-list/${id}/length`, { minutes }); toast.success("Length saved"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const inList = useMemo(() => new Set((rows || []).map((r) => r.id)), [rows]);
  const addable = props.filter((p) => !inList.has(p.id));
  const shown = (rows || []).filter((r) => `${r.address} ${r.clientName}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="communal-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-brand-ink flex items-center gap-2"><Repeat className="h-7 w-7 text-brand" />Communal area cleaning</h1>
        <p className="text-sm text-slate-500 mt-1">Properties with no fixed day. Turn on the "Communal area cleaning" layer on the Field Map to see them next to the day's routes.</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap justify-between">
        {canManage && <AddProperty all={addable} onAdd={(id) => toggle(id, true)} />}
        <div className="relative"><Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search list…" className="pl-9 h-10 w-[240px]" data-testid="communal-search" /></div>
      </div>
      <Card className="border-slate-200 overflow-hidden">
        {rows === null ? <p className="p-6 text-sm text-slate-400">Loading…</p> : !shown.length ? (
          <p className="p-6 text-sm text-slate-400" data-testid="communal-empty">No properties on the list yet.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            <div className="hidden md:grid grid-cols-[1fr_180px_110px_110px_110px_90px] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
              <span>Property</span><span>Client</span><span>Length (h)</span><span>Last visit</span><span>Next booked</span><span />
            </div>
            {shown.map((r) => (
              <div key={r.id} className="grid grid-cols-1 md:grid-cols-[1fr_180px_110px_110px_110px_90px] gap-1 items-center px-4 py-3 text-sm" data-testid={`communal-row-${r.id}`}>
                <Link to={`/properties/${r.id}`} className="font-semibold text-slate-800 hover:text-brand flex items-center gap-1.5"><MapPin className={`h-4 w-4 ${r.geo?.lat != null ? "text-violet-600" : "text-slate-300"}`} />{r.address}</Link>
                <span className="text-slate-600">{r.clientName}</span>
                {canManage ? (
                  <Input key={`${r.id}-${r.communalMinutes}`} type="number" min="0.25" step="0.25" defaultValue={(r.communalMinutes || 120) / 60} className="h-8 w-20" data-testid={`communal-length-${r.id}`}
                    onBlur={(e) => { if (Number(e.target.value) * 60 !== (r.communalMinutes || 120)) setLength(r.id, e.target.value); }} />
                ) : <span className="text-slate-600">{(r.communalMinutes || 120) / 60}h</span>}
                <span className="text-slate-600" data-testid={`communal-last-${r.id}`}><span className="md:hidden text-slate-400">Last visit: </span>{fmt(r.lastVisit)}</span>
                <span className={r.nextVisit ? "text-emerald-700 font-medium" : "text-slate-400"} data-testid={`communal-next-${r.id}`}><span className="md:hidden text-slate-400 font-normal">Next booked: </span>{fmt(r.nextVisit)}</span>
                {canManage ? <Button variant="ghost" size="sm" className="text-red-600 justify-self-start md:justify-self-end" data-testid={`communal-remove-${r.id}`} onClick={() => toggle(r.id, false)}><X className="h-4 w-4 mr-1" />Remove</Button> : <span />}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
